import type { MatrixData } from '../components/MatrixTable'

import { deleteScopedKey, readScopedString, readWaterJSON, writeScopedString, writeWaterJSON } from './waterStorage'
import { resolveSfrMatrixPresetKey } from './sfrMatrix'

const MORTALITY_MATRIX_STORAGE_KEY_PREFIX = 'mortalityMatrixData'
const MORTALITY_MATRIX_LOCAL_EDIT_MARKER_PREFIX = 'mortalityMatrixLocalEdit'
const LOCAL_EDIT_MAX_AGE_MS = 5 * 60 * 1000

const ZERO_SALMONID_AXES = {
  temperatures: [9, 10, 11, 12, 13, 14, 15, 16, 17],
  weights: [0.1, 0.45, 1.1, 2.8, 6.5, 8, 13, 22, 36, 80, 150, 300, 500, 1000]
}

const ZERO_TILAPIA_AXES = {
  temperatures: [16, 17, 18, 19, 20, 21, 22],
  weights: [0.1, 0.45, 1.1, 2.8, 6.5, 8, 13, 22, 36, 80, 150, 300, 500, 1000]
}

const ZERO_SHRIMP_AXES = {
  temperatures: [27, 32],
  weights: [0.002, 0.1, 0.5, 1, 3, 5, 10]
}

const buildZeroMatrix = (axes: { temperatures: number[]; weights: number[] }): MatrixData => ({
  temperatures: [...axes.temperatures],
  weights: [...axes.weights],
  values: axes.weights.map(() => axes.temperatures.map(() => 0))
})

const lerp = (from: number, to: number, ratio: number): number => from + ratio * (to - from)

// Bilinear fill from the matrix's four corners, interpolated by grid position
// (row/column index) rather than raw weight magnitude -- the weight axis
// spans several orders of magnitude non-uniformly, so an index-based blend
// is what "interpolate the values in between" means for a bracketed table
// like this one. Corner values are daily expected-mortality percentages
// supplied by the user as a real starting point for trout (previously an
// all-zero, indistinguishable-from-unconfigured default).
const buildCornerInterpolatedMatrix = (
  axes: { temperatures: number[]; weights: number[] },
  corners: { lowWeightLowTemp: number; lowWeightHighTemp: number; highWeightLowTemp: number; highWeightHighTemp: number }
): MatrixData => {
  const lastWeightIndex = axes.weights.length - 1
  const lastTempIndex = axes.temperatures.length - 1

  const values = axes.weights.map((_, weightIndex) => {
    const rowRatio = lastWeightIndex === 0 ? 0 : weightIndex / lastWeightIndex
    return axes.temperatures.map((_, tempIndex) => {
      const colRatio = lastTempIndex === 0 ? 0 : tempIndex / lastTempIndex
      const lowWeightEdge = lerp(corners.lowWeightLowTemp, corners.lowWeightHighTemp, colRatio)
      const highWeightEdge = lerp(corners.highWeightLowTemp, corners.highWeightHighTemp, colRatio)
      return lerp(lowWeightEdge, highWeightEdge, rowRatio)
    })
  })

  return {
    temperatures: [...axes.temperatures],
    weights: [...axes.weights],
    values
  }
}

const TROUT_DEFAULT_MATRIX = buildCornerInterpolatedMatrix(ZERO_SALMONID_AXES, {
  lowWeightLowTemp: 0.01,
  lowWeightHighTemp: 0.1,
  highWeightLowTemp: 0.005,
  highWeightHighTemp: 0.075
})

const DEFAULT_MATRICES = {
  global: buildZeroMatrix(ZERO_SALMONID_AXES),
  trout: TROUT_DEFAULT_MATRIX,
  salmon: buildZeroMatrix(ZERO_SALMONID_AXES),
  tilapia: buildZeroMatrix(ZERO_TILAPIA_AXES),
  shrimp: buildZeroMatrix(ZERO_SHRIMP_AXES)
} as const

export const cloneMatrix = (matrix: MatrixData): MatrixData => ({
  temperatures: [...matrix.temperatures],
  weights: [...matrix.weights],
  values: matrix.values.map((row) => [...row])
})

// A mortality matrix whose cells are all zero is indistinguishable from "never
// configured" — the species defaults (`buildZeroMatrix`) and the server's
// fallback for an unpersisted species both look exactly like this. Callers use
// this to show a distinct "not configured yet" hint so an all-zeros grid is
// never mistaken for measured 0% mortality (demo finding 4.4).
export const isBlankMatrix = (matrix: MatrixData | null | undefined): boolean => {
  if (!matrix || !Array.isArray(matrix.values) || matrix.values.length === 0) {
    return true
  }
  return matrix.values.every((row) =>
    !Array.isArray(row) || row.every((value) => !Number.isFinite(value) || value === 0)
  )
}

export const getDefaultMatrixForSpecies = (species: string): MatrixData => {
  const presetKey = resolveSfrMatrixPresetKey(species)
  const base = DEFAULT_MATRICES[presetKey] ?? DEFAULT_MATRICES.global
  return cloneMatrix(base)
}

const getStorageKeyForSpecies = (species: string) => `${MORTALITY_MATRIX_STORAGE_KEY_PREFIX}:${resolveSfrMatrixPresetKey(species)}`
const getLocalEditMarkerKeyForSpecies = (species: string) => `${MORTALITY_MATRIX_LOCAL_EDIT_MARKER_PREFIX}:${resolveSfrMatrixPresetKey(species)}`

export const loadCachedMatrix = (species: string): MatrixData | null => {
  try {
    const cached = readWaterJSON<MatrixData | null>(getStorageKeyForSpecies(species), null)
    if (!cached) return null

    return cloneMatrix({
      temperatures: Array.isArray(cached.temperatures) ? cached.temperatures : getDefaultMatrixForSpecies(species).temperatures,
      weights: Array.isArray(cached.weights) ? cached.weights : getDefaultMatrixForSpecies(species).weights,
      values: Array.isArray(cached.values) ? cached.values.map((row) => [...row]) : getDefaultMatrixForSpecies(species).values
    })
  } catch (error) {
    console.warn('Failed to read cached mortality matrix', error)
    return null
  }
}

export const cacheMatrix = (species: string, matrix: MatrixData) => {
  try {
    writeWaterJSON(getStorageKeyForSpecies(species), matrix)
  } catch (error) {
    console.warn('Failed to cache mortality matrix', error)
  }
}

export const markLocalMortalityMatrixEdit = (species: string) => {
  try {
    writeScopedString(getLocalEditMarkerKeyForSpecies(species), new Date().toISOString())
  } catch (error) {
    console.warn('Failed to mark local mortality matrix edit', error)
  }
}

export const clearLocalMortalityMatrixEdit = (species: string) => {
  deleteScopedKey(getLocalEditMarkerKeyForSpecies(species))
}

export const hasRecentLocalMortalityMatrixEdit = (species: string): boolean => {
  const raw = readScopedString(getLocalEditMarkerKeyForSpecies(species))
  if (!raw) return false

  const timestamp = Date.parse(raw)
  if (!Number.isFinite(timestamp)) return false
  return Date.now() - timestamp < LOCAL_EDIT_MAX_AGE_MS
}

const clamp = (value: number, minimum: number, maximum: number): number => {
  if (value < minimum) return minimum
  if (value > maximum) return maximum
  return value
}

const findBoundingIndexes = (values: number[], input: number): [number, number] => {
  if (values.length === 0) return [0, 0]
  if (input <= values[0]) return [0, 0]

  const lastIndex = values.length - 1
  if (input >= values[lastIndex]) return [lastIndex, lastIndex]

  for (let index = 0; index < lastIndex; index += 1) {
    const current = values[index]
    const next = values[index + 1]
    if (input >= current && input <= next) {
      return [index, index + 1]
    }
  }

  return [lastIndex, lastIndex]
}

export const interpolateMortalityRate = (
  weight: number,
  temperature: number,
  options: {
    species?: string
    matrix?: MatrixData | null
  } = {}
): number => {
  const matrix = options.matrix ?? getDefaultMatrixForSpecies(options.species ?? 'global')
  const weights = matrix.weights
  const temperatures = matrix.temperatures
  const values = matrix.values

  if (weights.length === 0 || temperatures.length === 0 || values.length === 0) {
    return 0
  }

  const clampedWeight = clamp(weight, weights[0], weights[weights.length - 1])
  const clampedTemperature = clamp(temperature, temperatures[0], temperatures[temperatures.length - 1])

  const [weightLowerIndex, weightUpperIndex] = findBoundingIndexes(weights, clampedWeight)
  const [tempLowerIndex, tempUpperIndex] = findBoundingIndexes(temperatures, clampedTemperature)

  const w1 = weights[weightLowerIndex]
  const w2 = weights[weightUpperIndex]
  const t1 = temperatures[tempLowerIndex]
  const t2 = temperatures[tempUpperIndex]

  const v11 = values[weightLowerIndex]?.[tempLowerIndex] ?? 0
  const v12 = values[weightLowerIndex]?.[tempUpperIndex] ?? v11
  const v21 = values[weightUpperIndex]?.[tempLowerIndex] ?? v11
  const v22 = values[weightUpperIndex]?.[tempUpperIndex] ?? v21

  let interpolated = v11

  if (w2 === w1 && t2 === t1) {
    interpolated = v11
  } else if (w2 === w1) {
    const ratio = t2 === t1 ? 0 : (clampedTemperature - t1) / (t2 - t1)
    interpolated = v11 + ratio * (v12 - v11)
  } else if (t2 === t1) {
    const ratio = w2 === w1 ? 0 : (clampedWeight - w1) / (w2 - w1)
    interpolated = v11 + ratio * (v21 - v11)
  } else {
    const weightRatio = (clampedWeight - w1) / (w2 - w1)
    const temperatureRatio = (clampedTemperature - t1) / (t2 - t1)
    const lowerInterpolation = v11 + weightRatio * (v21 - v11)
    const upperInterpolation = v12 + weightRatio * (v22 - v12)
    interpolated = lowerInterpolation + temperatureRatio * (upperInterpolation - lowerInterpolation)
  }

  if (!Number.isFinite(interpolated)) {
    return 0
  }

  return clamp(interpolated, 0, 100)
}